import { FormFieldInput } from '../../../../ui/gzForm/formField/component/FormFieldInput';
import { css, html, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { GzClearableInputWebComponent, InputWidthValues } from '../../../../ui/gzInput';
import { ButtonSize, FontSize } from '../../../../ui/gzIconButton/types';
import { CustomRuleValidator, getFileRuleInvalidExtensions } from '../common/validators';
import { GzInputErrorWebComponent } from '../../../../ui/gzForm/inputError';
import { GzIconButtonWebComponent } from '../../../../ui/gzIconButton';
import { IntegrityMonitorPlatform } from '../../../common/utils/types';

@customElement('gz-integrity-monitor-file-path-form-field')
export class IntegrityMonitorFilePathFormFieldWebComponent extends FormFieldInput<string[]> {
  @property({ type: Array, attribute: true, reflect: true })
  value: string[] = [];

  @property({ type: Number })
  maxValues = 32;

  @property({ type: String })
  placeholderFirst = '';

  @property({ type: String })
  placeholderSecond = '';

  @query('#input-prefix')
  public inputFirstComponent: GzClearableInputWebComponent;

  @query('#input-extension')
  public inputSecondComponent: GzClearableInputWebComponent;

  @query('#input-error')
  public inputError: GzInputErrorWebComponent;

  @query('gz-icon-button')
  public buttonComponent: GzIconButtonWebComponent;

  public validator: CustomRuleValidator<string> | null = null;

  public translations: any;

  public getOs: () => IntegrityMonitorPlatform;

  public getFormValue(): string[] {
    return this.value;
  }

  public setFormValue(value: string[]): void {
    this.value = value;
    this.clearInputComponents();
    this.emitChangeEvent();
  }

  public markAsInvalid(): void {
    super.markAsInvalid();
  }

  public markAsValid(): void {
    super.markAsValid();

    if (this.inputError) this.inputError.Error = null;
    if (this.inputFirstComponent) this.inputFirstComponent.markAsValid();
    if (this.inputSecondComponent) this.inputSecondComponent.markAsValid();
  }

  public removeInvalid(): void {
    if (!this.validator) return;

    if (this.value.find(path => this.validator && !this.validator(path).valid) !== undefined) {
      this.value = this.value.filter(path => this.validator && this.validator(path).valid);
      this.emitChangeEvent();
    }
  }

  static get styles() {
    return css`
      .wrapper {
        display: flex;
        align-items: center;
      }

      #chip-container {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        cursor: default;
        width: 700px;
        margin-top: 8px;

        &.disabled {
          pointer-events: none;
        }
      }

      .mr {
        margin-right: 16px;
      }
    `;
  }

  private renderItems(text: string): TemplateResult {
    return html` <gz-chip
      text=${text}
      notSelectable
      removable
      ?disabled="${this.disabled}"
      @removeChip="${() => this.handleRemoveItem(text)}"
    ></gz-chip>`;
  }

  render() {
    return html`
      <div class="wrapper">
        <gz-input
          class="mr"
          id="input-prefix"
          placeholder="${this.placeholderFirst}"
          inputwidth="${InputWidthValues.LARGE}"
          @change="${this.handleChange}"
          ?disabled="${this.disabled}"
        ></gz-input>
        <gz-input
          class="mr"
          id="input-extension"
          placeholder="${this.placeholderSecond}"
          inputwidth="${InputWidthValues.MEDIUM}"
          ?disabled="${this.disabled}"
        ></gz-input>
        <gz-icon-button
          buttontype="square"
          buttonfill="primary"
          icon="ds-icon-add"
          size="${ButtonSize.SIZE_32}"
          fontSize="${FontSize.ICON_SIZE_20}"
          disabled
          @click="${this.handleClick}"
          ?disabled="${this.disabled}"
        ></gz-icon-button>
      </div>
      <gz-input-error id="input-error"></gz-input-error>
      <div id="chip-container" class="${this.disabled ? 'disabled' : ''}">${this.value.map(chip => this.renderItems(chip))}</div>
    `;
  }

  public refresh() {
    this.inputError.Error = null;
    this.inputFirstComponent.markAsValid();
    this.inputSecondComponent.markAsValid();
    this.handleChange();
  }

  private handleChange() {
    this.buttonComponent.disabled =
      this.getOs().toString() === '' ||
      this.inputFirstComponent.value.length === 0 ||
      this.value.length >= this.maxValues;
  }

  private handleClick(e: Event) {
    let inputFirst = this.inputFirstComponent.value;
    const inputSecond = this.inputSecondComponent.value;

    if (inputFirst.length === 0) {
      this.inputFirstComponent.markAsInvalid();
      this.inputSecondComponent.markAsValid();
      this.inputError.Error = this.translations.errorFileRulePrefixIsRequired;
      return;
    }

    if (inputFirst.endsWith('\\')) {
      inputFirst = inputFirst + '*';
    }

    const isPlatformWindows = this.getOs() === IntegrityMonitorPlatform.WINDOWS;
    if (isPlatformWindows && inputFirst.endsWith('\\*') && inputSecond === '') {
      this.inputFirstComponent.markAsValid();
      this.inputSecondComponent.markAsInvalid();
      this.inputError.Error = this.translations.errorFileRuleExtensionIsRequired;
      return;
    }

    const invalidExtensions = getFileRuleInvalidExtensions();
    if (invalidExtensions.find((x: string) => inputSecond.endsWith(x))) {
      this.inputFirstComponent.markAsValid();
      this.inputSecondComponent.markAsInvalid();
      this.inputError.Error = this.translations.errorFileRuleInvalidExtension;
      return;
    }

    if (!this.validateWildcards(inputFirst, inputSecond)) {
      return;
    }

    const value = inputSecond ? inputFirst + inputSecond : inputFirst;
    if (this.validator) {
      const res = this.validator(value);
      if (!res.valid) {
        this.inputError.Error = res.error;
        this.inputFirstComponent.markAsInvalid();
        this.inputSecondComponent.markAsInvalid();
        return;
      }
    }

    if (this.value.find(x => x === value)) {
      this.inputFirstComponent.markAsInvalid();
      this.inputSecondComponent.markAsInvalid();
      this.inputError.Error = this.translations.errorDuplicateValueIntroduced;
      return;
    }

    const newItems = this.value.slice();
    newItems.push(value);
    this.value = newItems;

    this.clearInputComponents();

    this.inputFirstComponent.markAsValid();
    this.inputSecondComponent.markAsValid();
    this.inputError.Error = null;

    this.emitChangeEvent();
  }

  private handleRemoveItem(item: string) {
    const newItems = this.value.slice();
    newItems.splice(newItems.indexOf(item), 1);
    this.value = newItems;

    this.buttonComponent.disabled = this.inputFirstComponent.value.length === 0 || this.value.length >= this.maxValues;

    this.emitChangeEvent();
  }

  private emitChangeEvent() {
    const event = new CustomEvent('change', {
      detail: {
        value: this.value,
      },
      bubbles: true,
      cancelable: true,
      composed: false,
    });

    this.dispatchEvent(event);
  }

  private clearInputComponents() {
    this.inputFirstComponent.clearInputValue(new Event('clear'));
    this.inputSecondComponent.clearInputValue(new Event('clear'));
  }

  private validateWildcards(inputPrefix: string, inputExtension: string): boolean {
    // check that, if prefix has wildcard, it is only one, and it is at the end of the string
    const indexOfWildcard = inputPrefix.indexOf('*');
    if (indexOfWildcard !== -1 && indexOfWildcard !== inputPrefix.length - 1) {
      this.inputError.Error = this.translations.errorFileRuleInvalidWildcard;
      this.inputFirstComponent.markAsInvalid();
      this.inputSecondComponent.markAsValid();
      return false;
    }

    // check that extension does not have any wildcard
    if (inputExtension.indexOf('*') !== -1) {
      this.inputError.Error = this.translations.errorFileRuleInvalidWildcard;
      this.inputFirstComponent.markAsValid();
      this.inputSecondComponent.markAsInvalid();
      return false;
    }

    return true;
  }
}
